import "core-js/modules/web.dom-collections.iterator.js";
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Form } from 'react-final-form';
import { IconButton, LinkButton, StandardButton } from '@eika/button';
import { IconTypes } from '@eika/icon';
import { TaxRelationType, YesNoAnswer } from '../generated-types';
import { DELETED_ROW_ID } from '../types/list-question-type';
import SelectInput from '../types/select-input';
import StandaloneYesNoQuestionType from '../types/standalone-yes-no-question-type';
import { StandardInputField } from '../types/standard-input-field';
import NonUsTaxRelationsHelpModal from '../util/helpModals/non-us-tax-relations-help-modal';
import UsTaxRelationsHelpModal from '../util/helpModals/us-tax-relations-help-modal';
import UsTaxRelationsToggleHelpModal from '../util/helpModals/us-tax-relations-toggle-help-modal';
import { composeValidators, focusOnErrors, isCountrySwedenAndValidSwedishTIN, isValidTINLength, maxLengthValidator, requiredField, valueNotAllowed } from '../util/validator';
import OverlayLoader from './overlay-loader';
import TaxConfirmation from './tax-confirmation';
const TaxRelationSection = _ref => {
  var _taxCountries$countri, _taxCountries$countri2;
  let {
    taxRelationsSection,
    submitButtonText,
    taxCountries,
    submitTaxRelations,
    onSubmit,
    getTaxRelationCountries,
    submitTaxRelationSection,
    error
  } = _ref;
  const [nonUSRelationRows, setNonUSRelationRows] = useState([]);
  const [lastUsedIndex, setLastUsedIndex] = useState(0);
  const [loading, setLoading] = useState(false);
  const [confirmNotifyBank, setConfirmNotifyBank] = useState(false);
  const [hasClickedSubmit, setHasClickedSubmit] = useState(false);
  const {
    taxRelationTexts
  } = taxRelationsSection;
  useEffect(() => {
    if (!taxCountries.loaded && !taxCountries.fetching) {
      getTaxRelationCountries();
    }
  }, [taxCountries.loaded, taxCountries.fetching, getTaxRelationCountries]);
  useEffect(() => {
    if (submitTaxRelations.submitted) {
      setLoading(false);
      onSubmit();
    }
  }, [submitTaxRelations.submitted, onSubmit]);
  const addRow = useCallback(() => {
    const id = "".concat(Math.random()).slice(2).padEnd(20, '0');
    const newIndex = lastUsedIndex + 1;
    setLastUsedIndex(newIndex);
    const newRow = {
      id,
      index: newIndex
    };
    setNonUSRelationRows([...nonUSRelationRows, newRow]);
  }, [nonUSRelationRows, lastUsedIndex]);
  const getValues = taxRelationList => {
    const values = {};
    let numberOfNonUSTaxRelations = 0;
    const newRows = [];
    taxRelationList.filter(taxRelation => taxRelation.country.iso3 !== 'USA').forEach((taxRelation, index) => {
      values["_".concat(index)] = {
        isFromServer: true,
        hasValidGin: taxRelation.type === TaxRelationType.WITH_OUT_TAX_NUMBER ? YesNoAnswer.NO : YesNoAnswer.YES,
        missingCrsNumber: taxRelation.reasonForMissingTaxNumber,
        crsNumber: taxRelation.taxNumber,
        whatCountryIsTheTaxRelation: taxRelation.country.iso3,
        createdDate: taxRelation.createdDate
      };
      const id = "".concat(Math.random()).slice(2).padEnd(20, '0');
      const newIndex = numberOfNonUSTaxRelations + 1;
      const newRow = {
        id,
        index: newIndex
      };
      newRows.push(newRow);
      numberOfNonUSTaxRelations += 1;
    });
    setLastUsedIndex(numberOfNonUSTaxRelations);
    setNonUSRelationRows(newRows);
    return values;
  };
  const setInitialAmericanTaxRelationIfExists = (values, taxRelationList) => {
    const americanTaxRelation = taxRelationList.find(taxRelation => taxRelation.country.iso3 === 'USA');
    if (americanTaxRelation) {
      values.hasAmericanTaxRelation = YesNoAnswer.YES;
      values.hasValidTin = americanTaxRelation.type === TaxRelationType.WITH_OUT_TAX_NUMBER ? YesNoAnswer.NO : YesNoAnswer.YES;
      values.missingFatcaNumber = americanTaxRelation.reasonForMissingTaxNumber;
      values.fatcaNumber = americanTaxRelation.taxNumber;
      values.isFromServer = true;
    }
  };
  const getInitialValues = () => {
    if (taxRelationsSection.taxRelationList && taxRelationsSection.taxRelationList.length > 0) {
      const values = getValues(taxRelationsSection.taxRelationList);
      if (values && Object.keys(values).find(key => key.startsWith('_'))) {
        values.hasInternationalTaxRelation = YesNoAnswer.YES;
      }
      setInitialAmericanTaxRelationIfExists(values, taxRelationsSection.taxRelationList);
      return values;
    }
    return undefined;
  };
  const initialValues = useMemo(getInitialValues, [taxRelationsSection.taxRelationList]);
  useEffect(() => {
    if (nonUSRelationRows.length === 0) {
      addRow();
    }
  }, [addRow, nonUSRelationRows.length]);
  const onRemove = (id, setValueOfField) => {
    const indexToRemove = nonUSRelationRows.findIndex(row => id === row);
    if (indexToRemove >= 0) {
      const newRows = [...nonUSRelationRows];
      newRows[indexToRemove].id = DELETED_ROW_ID;
      setNonUSRelationRows(newRows);
      if (setValueOfField) {
        setValueOfField({
          key: "_".concat(indexToRemove),
          value: undefined
        });
      }
    }
  };
  const countries = (_taxCountries$countri = (_taxCountries$countri2 = taxCountries.countries) === null || _taxCountries$countri2 === void 0 || (_taxCountries$countri2 = _taxCountries$countri2.crs) === null || _taxCountries$countri2 === void 0 ? void 0 : _taxCountries$countri2.map((country, i) => {
    return {
      text: country.text,
      value: country.iso3,
      key: (i + 1).toString()
    };
  })) !== null && _taxCountries$countri !== void 0 ? _taxCountries$countri : [];
  const getFatcaFields = (hasValidTin, isFromServer) => {
    if (hasValidTin) {
      if (hasValidTin === YesNoAnswer.YES) {
        return /*#__PURE__*/React.createElement(StandardInputField, {
          label: taxRelationTexts.fatcaNumber,
          name: "fatcaNumber",
          validate: composeValidators(requiredField, isValidTINLength)
        });
      }
      if (hasValidTin === YesNoAnswer.NO) {
        return /*#__PURE__*/React.createElement(StandardInputField, {
          label: taxRelationTexts.missingFatcaNumber,
          name: "missingFatcaNumber",
          validate: composeValidators(requiredField, value => maxLengthValidator(value, 200, isFromServer))
        });
      }
    }
    return undefined;
  };
  const getTinLabelTextWithHelp = () => /*#__PURE__*/React.createElement("span", null, taxRelationTexts.hasValidTin, /*#__PURE__*/React.createElement(UsTaxRelationsHelpModal, null));
  const getAmericanTaxRelation = values => {
    if (values.hasAmericanTaxRelation === YesNoAnswer.YES) {
      return /*#__PURE__*/React.createElement("div", {
        className: "sub-question-wrapper"
      }, /*#__PURE__*/React.createElement(StandaloneYesNoQuestionType, {
        selected: values.hasValidTin,
        label: taxRelationTexts.hasValidTin,
        name: "hasValidTin",
        validate: requiredField,
        overrideLabel: getTinLabelTextWithHelp()
      }), /*#__PURE__*/React.createElement("div", {
        className: "form-question"
      }, getFatcaFields(values.hasValidTin, values.isFromServer)), values.isFromServer ? /*#__PURE__*/React.createElement(StandaloneYesNoQuestionType, {
        label: "Er denne skatterelasjonen fortsatt gjeldende?",
        validate: requiredField,
        name: "taxRelationEndedUS",
        selected: values.taxRelationEndedUS
      }) : undefined);
    }
    return undefined;
  };
  const getCrsFields = (hasValidGin, whatCountryIsTheTaxRelation, isFromServer, index) => {
    if (hasValidGin) {
      if (hasValidGin === YesNoAnswer.YES) {
        return /*#__PURE__*/React.createElement(StandardInputField, {
          label: taxRelationTexts.crsNumber,
          name: "_".concat(index, ".crsNumber"),
          validate: composeValidators(requiredField, isValidTINLength, value => isCountrySwedenAndValidSwedishTIN(value, whatCountryIsTheTaxRelation))
        });
      }
      if (hasValidGin === YesNoAnswer.NO) {
        return /*#__PURE__*/React.createElement(StandardInputField, {
          label: taxRelationTexts.missingCrsNumber,
          name: "_".concat(index, ".missingCrsNumber"),
          validate: composeValidators(requiredField, value => maxLengthValidator(value, 200, isFromServer))
        });
      }
    }
    return undefined;
  };
  const getRows = (values, setValueOfField) => {
    return nonUSRelationRows.map((row, i) => {
      if (row.id !== DELETED_ROW_ID) {
        var _values, _values2, _values3, _values4, _values5, _values6, _values7, _values8;
        return /*#__PURE__*/React.createElement("div", {
          key: row.index,
          className: "tax-country-wrapper"
        }, (_values = values["_".concat(i)]) !== null && _values !== void 0 && _values.isFromServer ? undefined : /*#__PURE__*/React.createElement(IconButton, {
          className: "sgw-button remove-icon",
          id: "removeItem",
          onClick: () => {
            onRemove(row, setValueOfField);
          },
          icon: IconTypes.KRYSS_24,
          screenReaderText: "fjern land"
        }), /*#__PURE__*/React.createElement(SelectInput, {
          label: taxRelationTexts.whatCountryIsTheTaxRelation,
          name: "_".concat(i, ".whatCountryIsTheTaxRelation"),
          values: [{
            text: 'Velg',
            value: 'velg',
            key: '0'
          }, ...countries],
          validate: composeValidators(requiredField, value => valueNotAllowed(value, 'velg', 'Velg')),
          disabled: (_values2 = values["_".concat(i)]) === null || _values2 === void 0 ? void 0 : _values2.isFromServer
        }), /*#__PURE__*/React.createElement(StandaloneYesNoQuestionType, {
          selected: (_values3 = values["_".concat(i)]) === null || _values3 === void 0 ? void 0 : _values3.hasValidGin,
          label: taxRelationTexts.hasValidGin,
          name: "_".concat(i, ".hasValidGin"),
          validate: requiredField
        }), /*#__PURE__*/React.createElement("div", {
          className: "form-question"
        }, getCrsFields((_values4 = values["_".concat(i)]) === null || _values4 === void 0 ? void 0 : _values4.hasValidGin, (_values5 = values["_".concat(i)]) === null || _values5 === void 0 ? void 0 : _values5.whatCountryIsTheTaxRelation, (_values6 = values["_".concat(i)]) === null || _values6 === void 0 ? void 0 : _values6.isFromServer, i)), (_values7 = values["_".concat(i)]) !== null && _values7 !== void 0 && _values7.isFromServer ? /*#__PURE__*/React.createElement(StandaloneYesNoQuestionType, {
          label: "Er denne skatterelasjonen fortsatt gjeldende?",
          validate: requiredField,
          name: "_".concat(i, ".taxRelationEnded"),
          selected: (_values8 = values["_".concat(i)]) === null || _values8 === void 0 ? void 0 : _values8.taxRelationEnded
        }) : undefined);
      }
      return undefined;
    });
  };
  const getInternationalTaxRelations = (values, setValueOfField) => {
    if (values.hasInternationalTaxRelation === YesNoAnswer.YES) {
      return /*#__PURE__*/React.createElement("div", {
        id: "internationalTaxRelations"
      }, getRows(values, setValueOfField), /*#__PURE__*/React.createElement(LinkButton, {
        id: "addMore",
        icon: IconTypes.PLUSS_24,
        onClick: addRow
      }, "Legg til"));
    }
    return undefined;
  };
  const getFatcaObject = values => {
    var _taxCountries$countri3;
    let object;
    if (values.hasValidTin === YesNoAnswer.YES) {
      object = {};
      object.taxNumber = values.fatcaNumber;
      object.type = TaxRelationType.WITH_TAX_NUMBER;
    } else {
      object = {};
      object.reasonForMissingTaxNumber = values.missingFatcaNumber;
      object.type = TaxRelationType.WITH_OUT_TAX_NUMBER;
    }
    object.country = (_taxCountries$countri3 = taxCountries.countries) === null || _taxCountries$countri3 === void 0 ? void 0 : _taxCountries$countri3.fatca;
    object.createdDate = values.createdDate ? values.createdDate : new Date();
    object.endedDate = values.taxRelationEndedUS === YesNoAnswer.NO ? new Date() : null;
    return object;
  };
  const getCountryFromIso3 = country => {
    var _taxCountries$countri4;
    return (_taxCountries$countri4 = taxCountries.countries) === null || _taxCountries$countri4 === void 0 || (_taxCountries$countri4 = _taxCountries$countri4.crs) === null || _taxCountries$countri4 === void 0 ? void 0 : _taxCountries$countri4.find(element => element.iso3 === country);
  };
  const addCrsTaxRelations = (taxRelations, values) => {
    const keysStartingWithUnderscore = Object.keys(values).filter(key => key.startsWith('_'));
    if ((keysStartingWithUnderscore === null || keysStartingWithUnderscore === void 0 ? void 0 : keysStartingWithUnderscore.length) > 0) {
      keysStartingWithUnderscore.forEach(key => {
        const taxRelation = values[key];
        if (taxRelation.hasValidGin === YesNoAnswer.YES) {
          taxRelations.push({
            country: getCountryFromIso3(taxRelation.whatCountryIsTheTaxRelation),
            taxNumber: taxRelation.crsNumber,
            createdDate: taxRelation.createdDate ? taxRelation.createdDate : new Date(),
            endedDate: taxRelation.taxRelationEnded === YesNoAnswer.NO ? new Date() : null,
            type: TaxRelationType.WITH_TAX_NUMBER
          });
        } else {
          taxRelations.push({
            country: getCountryFromIso3(taxRelation.whatCountryIsTheTaxRelation),
            reasonForMissingTaxNumber: taxRelation.missingCrsNumber,
            createdDate: taxRelation.createdDate ? taxRelation.createdDate : new Date(),
            endedDate: taxRelation.taxRelationEnded === YesNoAnswer.NO ? new Date() : null,
            type: TaxRelationType.WITH_OUT_TAX_NUMBER
          });
        }
      });
    }
  };
  const getObjectToSubmit = values => {
    const {
      hasAmericanTaxRelation,
      hasInternationalTaxRelation
    } = values;
    const taxRelations = [];
    if (hasAmericanTaxRelation === YesNoAnswer.YES) {
      taxRelations.push(getFatcaObject(values));
    }
    if (hasInternationalTaxRelation === YesNoAnswer.YES) {
      addCrsTaxRelations(taxRelations, values);
    }
    return taxRelations;
  };
  const submitAnswers = values => {
    setHasClickedSubmit(true);
    if (confirmNotifyBank) {
      setLoading(true);
      submitTaxRelationSection(getObjectToSubmit(values));
    }
  };
  const getAmericanTaxRelationToggleTextWithHelp = () => /*#__PURE__*/React.createElement("span", null, taxRelationTexts.hasAmericanTaxRelation, /*#__PURE__*/React.createElement(UsTaxRelationsToggleHelpModal, null));
  const getNonAmericanTaxRelationTextWithHelp = () => /*#__PURE__*/React.createElement("span", null, taxRelationTexts.hasInternationalTaxRelation, /*#__PURE__*/React.createElement(NonUsTaxRelationsHelpModal, null));
  const getAmericanTaxRelationYesNo = values => {
    const {
      isFromServer,
      hasAmericanTaxRelation
    } = values;
    if (isFromServer && hasAmericanTaxRelation === YesNoAnswer.YES) {
      return /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement("div", {
        className: "disabled-yes-no-question"
      }, getAmericanTaxRelationToggleTextWithHelp()), /*#__PURE__*/React.createElement("span", {
        className: "small-margin-left"
      }, "Ja"));
    }
    return /*#__PURE__*/React.createElement(StandaloneYesNoQuestionType, {
      selected: hasAmericanTaxRelation,
      label: taxRelationTexts.hasAmericanTaxRelation,
      name: "hasAmericanTaxRelation",
      validate: requiredField,
      overrideLabel: getAmericanTaxRelationToggleTextWithHelp()
    });
  };
  const getNonAmericanTaxRelationYesNo = values => {
    const {
      isFromServer,
      hasInternationalTaxRelation
    } = values;
    if (isFromServer && hasInternationalTaxRelation === YesNoAnswer.YES) {
      return /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement("div", {
        className: "disabled-yes-no-question"
      }, getNonAmericanTaxRelationTextWithHelp()), /*#__PURE__*/React.createElement("span", {
        className: "small-margin-left"
      }, "Ja"));
    }
    return /*#__PURE__*/React.createElement(StandaloneYesNoQuestionType, {
      selected: hasInternationalTaxRelation,
      label: taxRelationTexts.hasInternationalTaxRelation,
      name: "hasInternationalTaxRelation",
      validate: requiredField,
      overrideLabel: getNonAmericanTaxRelationTextWithHelp()
    });
  };
  return /*#__PURE__*/React.createElement(React.Fragment, null, loading && !error ? /*#__PURE__*/React.createElement(OverlayLoader, null) : undefined, /*#__PURE__*/React.createElement("h2", {
    className: "header-title"
  }, taxRelationTexts.headerText), /*#__PURE__*/React.createElement(Form, {
    onSubmit: submitAnswers,
    decorators: [focusOnErrors],
    initialValues: initialValues,
    mutators: {
      setValueOfField: (args, state, utils) => {
        utils.changeValue(state, args[0].key, () => args[0].value);
      }
    }
  }, props => {
    return /*#__PURE__*/React.createElement("form", {
      onSubmit: props.handleSubmit
    }, /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement("div", {
      className: "form-question"
    }, getAmericanTaxRelationYesNo(props.values), " ", getAmericanTaxRelation(props.values)), /*#__PURE__*/React.createElement("div", {
      className: "form-question"
    }, getNonAmericanTaxRelationYesNo(props.values), ' ', getInternationalTaxRelations(props.values, props.form.mutators.setValueOfField))), /*#__PURE__*/React.createElement(TaxConfirmation, {
      onValueChange: setConfirmNotifyBank,
      showError: hasClickedSubmit,
      text: taxRelationTexts
    }), /*#__PURE__*/React.createElement(StandardButton, {
      className: "progress-next-button",
      type: "submit"
    }, submitButtonText));
  }));
};
export default TaxRelationSection;